import { FillableColumn } from '@/components/UI/DataTable/DataTable.model.ts';
import { DateTimePickerNative } from '@/components/UI/DateTimePickerNative/DateTimePickerNative.tsx';
import { Duration, normalize as normalizeDuration } from '@/components/UI/FormElements/Duration/Duration.tsx';
import Radio from '@/components/UI/FormElements/Radio/Radio.tsx';
import Switch from '@/components/UI/FormElements/Switch';
import { errorToast } from '@/helpers.tsx';
import { _notNil } from '@/littledash.ts';
import cn from 'classnames';
import type { DataTableColumnFillRequestV1, DataTableColumnFillType } from '@/model/DataTable.model.ts';
import { DateUtils } from '@/utils/Date.utils.ts';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal.tsx';
import { roundToNearestHours } from 'date-fns';
import { FC, useMemo } from 'react';
import { Controller, useForm, useWatch, Validate } from 'react-hook-form@latest';
import InVivoError from '@/model/InVivoError';

interface FormData {
  fillMode: 'planned' | 'manual';
  start: string;
  duration: string;
  overwrite: boolean;
}

const durationValidator: Validate<string, FormData> = (value) => {
  const result = DateUtils.validateDurationRange(value, {
    allow_zero: true,
    allow_negative: false,
  });

  if (!result.valid) {
    switch (result.reason) {
      case 'error_format':
        return 'Invalid duration';
      case 'error_range_min':
        return 'Duration cannot be negative';
      case 'error_range_max':
        return 'Duration exceeds maximum allowed';
    }
  }

  return true;
};

interface DataTableColumnFillProps {
  column: FillableColumn;
  showPlannedTimeOption?: boolean;
  onFill: (data: DataTableColumnFillRequestV1) => Promise<void>;
  onCancel: () => void;
}

export const DataTableColumnFill: FC<DataTableColumnFillProps> = ({
  column,
  showPlannedTimeOption = false,
  onFill,
  onCancel,
}) => {
  const formMethods = useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      fillMode: 'manual',
      start: roundToNearestHours(new Date()).toISOString(),
      duration:
        column.type === 'timestampBaseline' && _notNil(column.pk?.interval)
          ? normalizeDuration({ minutes: Math.floor(column.pk.interval / 60) })
          : 'PT30M',
      overwrite: false,
    },
  });
  const fillData = useMemo<{ type: DataTableColumnFillType; label: string }>(() => {
    switch (column.type) {
      case 'sampleCollectedAt':
        return { type: 'sample_collected_at', label: 'Time between collections' };
      case 'sampleDoseTime':
        return { type: 'sample_dose_time', label: 'Time between doses' };
      case 'timestampBaseline':
        return { type: 'pk_dose', label: 'Time between doses' };
    }
    // @ts-expect-error: exhaustive check
    throw new InVivoError('Unsupported column type', { level: 'fatal', context: { type: column.type } });
  }, [column]);
  const fillMode = useWatch({ control: formMethods.control, name: 'fillMode' });

  const showManualFields = !showPlannedTimeOption || fillMode === 'manual';
  const handleSubmit = async (formData: FormData) => {
    try {
      const includeTiming = showManualFields;
      await onFill({
        type: fillData.type,
        overwrite: formData.overwrite,
        ...(includeTiming && { start: formData.start, duration: formData.duration }),
      });
    } catch {
      errorToast('Could not fill column');
    }
  };
  return (
    <ModalContainer size="narrow">
      <ModalHeader title="Fill column" />
      <form
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        data-test-component="DataTableColumnFill"
        data-test-element="form"
      >
        {showPlannedTimeOption && (
          <Controller
            control={formMethods.control}
            name="fillMode"
            render={({ field }) => (
              <div className="pa3" data-test-element="fill-mode-field">
                <Radio
                  id="fill-mode-planned"
                  className="pb2"
                  value="planned"
                  name={field.name}
                  label="Use planned time"
                  checked={field.value === 'planned'}
                  onChange={() => field.onChange('planned')}
                />
                <Radio
                  id="fill-mode-manual"
                  value="manual"
                  name={field.name}
                  label="Define start and intervals"
                  checked={field.value === 'manual'}
                  onChange={() => field.onChange('manual')}
                />
              </div>
            )}
          />
        )}
        {showManualFields && (
          <>
            <Controller
              control={formMethods.control}
              name="start"
              shouldUnregister
              render={({ field }) => (
                <div
                  className={cn('pa3 flex justify-between flex-wrap items-center align-center', {
                    'bt b--moon-gray': showPlannedTimeOption,
                  })}
                  data-test-element="start-field"
                >
                  <label>Start time</label>
                  <DateTimePickerNative
                    style={{ marginBottom: 0 }}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </div>
              )}
            />
            <Controller
              control={formMethods.control}
              name="duration"
              shouldUnregister
              rules={{ validate: durationValidator }}
              render={({ field, fieldState }) => (
                <div
                  className="pa3 bt b--moon-gray flex justify-between flex-wrap items-center align-center"
                  data-test-element="duration-field"
                >
                  <label>{fillData.label}</label>
                  <Duration
                    value={field.value}
                    disabled={field.disabled}
                    error={fieldState.invalid}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </div>
              )}
            />
          </>
        )}
        <Controller
          control={formMethods.control}
          name="overwrite"
          render={({ field }) => (
            <div
              className="pa3 bt b--moon-gray flex justify-between flex-wrap items-center align-center"
              data-test-element="overwrite-field"
            >
              <span>
                <label>Overwrite existing values</label>
                <small>All timestamps in this column will be replaced</small>
              </span>
              <Switch value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
            </div>
          )}
        />
      </form>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        submitBtnText="Fill"
        submitButtonProps={{
          disabled: !formMethods.formState.isValid && formMethods.formState.isDirty,
        }}
        onCancel={onCancel}
        cancelBtnText="Cancel"
      />
    </ModalContainer>
  );
};
